import {Alert} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import type {FC, ReactNode} from 'react';

import classes from './InfoAlert.module.css';

export type InfoAlertVariant = 'info' | 'success' | 'warning';

/**
 * Generic informational banner — blue info-circle by default, a green success
 * variant, or a yellow warning variant.
 * @registry Blue informational / green success / yellow warning alert with a default icon per variant.
 */
export interface InfoAlertProps {
  children: ReactNode;
  /** Optional Mantine spacing applied below the alert. */
  mb?: string;
  /** Optional alert title. */
  title?: string;
  /** `info` (default, blue), `success` (green) or `warning` (yellow.9). */
  variant?: InfoAlertVariant;
}

const VARIANT_CONFIG: Record<InfoAlertVariant, {color: string; icon: ReactNode}> = {
  info: {color: 'blue', icon: <IconInfoCircle />},
  success: {color: 'green', icon: <IconCircleCheck />},
  // yellow.9 — yellow.8 fails contrast on white.
  warning: {color: 'yellow.9', icon: <IconAlertTriangle />},
};

export const InfoAlert: FC<InfoAlertProps> = ({
  children,
  mb,
  title,
  variant = 'info',
}) => {
  const {color, icon} = VARIANT_CONFIG[variant];
  return (
    <Alert
      color={color}
      icon={icon}
      title={title}
      mb={mb}
      classNames={{message: classes.message}}>
      {children}
    </Alert>
  );
};
